interface DiscoverChallengeCardProps {
  title: string;
  description: string;
  icon: string;
  duration: string;
  stake: string;
  players: string;
  onClick?: () => void;
  variant?: "default" | "active";
}

export function DiscoverChallengeCard({
  title,
  description,
  icon,
  duration,
  stake,
  players,
  onClick,
  variant = "default",
}: DiscoverChallengeCardProps) {
  const handleViewClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    onClick?.();
  };

  // Use active challenge card colors when variant is "active"
  const cardClassName =
    variant === "active"
      ? "flex flex-col overflow-hidden rounded-3xl bg-background-light dark:bg-background-dark shadow-md dark:shadow-sm dark:shadow-primary/20 cursor-pointer hover:shadow-lg dark:hover:shadow-primary/30 transition-shadow"
      : "flex flex-col overflow-hidden rounded-3xl bg-secondary-light dark:bg-secondary-dark cursor-pointer transition-transform active:scale-[0.98]";

  const borderClassName =
    variant === "active"
      ? "flex items-center justify-between gap-4 border-t border-slate-200 dark:border-slate-700 p-4"
      : "flex items-center justify-between gap-4 border-t border-background-light dark:border-background-dark p-4";

  const titleClassName =
    variant === "active"
      ? "text-lg font-bold leading-tight text-slate-900 dark:text-white line-clamp-1"
      : "text-lg font-bold leading-tight text-slate-900 dark:text-white line-clamp-1";

  const descriptionClassName =
    variant === "active"
      ? "text-sm text-slate-500 dark:text-slate-400 line-clamp-2"
      : "text-sm text-slate-500 dark:text-slate-400 line-clamp-2";

  const statLabelClassName =
    variant === "active"
      ? "text-xs text-slate-500 dark:text-slate-400"
      : "text-xs text-slate-500 dark:text-slate-400";

  const statValueClassName =
    variant === "active"
      ? "text-sm font-bold truncate text-slate-900 dark:text-white"
      : "text-sm font-bold truncate text-slate-900 dark:text-white";

  const iconCircleClassName =
    variant === "active"
      ? "flex size-12 shrink-0 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800 text-primary"
      : "flex size-12 shrink-0 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800 text-primary";

  return (
    <div className={cardClassName} onClick={onClick}>
      {/* Top Section - Title, Description, Icon */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-1">
            <p className={titleClassName}>{title}</p>
            <p className={descriptionClassName}>{description}</p>
          </div>

          {/* Icon Circle */}
          <div className={iconCircleClassName}>
            <span className="material-symbols-outlined text-3xl">{icon}</span>
          </div>
        </div>
      </div>

      {/* Bottom Section - Stats and Join Button */}
      <div className={borderClassName}>
        <div className="grid grid-cols-3 gap-2 flex-1 min-w-0">
          {/* Duration */}
          <div className="flex flex-col items-start min-w-0">
            <span className={statLabelClassName}>Duration</span>
            <p className={statValueClassName}>{duration}</p>
          </div>

          {/* Stake */}
          <div className="flex flex-col items-start min-w-0">
            <span className={statLabelClassName}>Stake</span>
            <p className={statValueClassName}>{stake}</p>
          </div>

          {/* Players */}
          <div className="flex flex-col items-start min-w-0">
            <span className={statLabelClassName}>Players</span>
            <p className={statValueClassName}>{players}</p>
          </div>
        </div>

        {/* View Button */}
        <button
          onClick={handleViewClick}
          className="flex h-10 items-center justify-center rounded-full bg-primary px-5 text-sm font-bold text-black"
        >
          View
        </button>
      </div>
    </div>
  );
}
